import { getAccessToken, getApiBaseUrl } from './apiClient';

// The owner preview page iframes this chat on the same origin. A third-party
// site cannot pass this check, so the signed-in session stays off their page.
const appOwnsFrame = () => {
  if (window.parent === window) return true;
  try {
    return window.parent.location.origin === window.location.origin;
  } catch {
    return false;
  }
};

const parentOrigin = () => {
  try {
    if (document.referrer) return new URL(document.referrer).origin;
  } catch {
    // Ignore a malformed referrer and fall through.
  }
  if (appOwnsFrame()) return window.location.origin;
  return '';
};

const embedHeaders = async (publicKey, { captchaToken, captchaPass } = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    'X-Embed-Key': publicKey,
  };
  const parent = parentOrigin();
  if (parent) headers['X-Embed-Parent-Origin'] = parent;
  if (captchaToken) headers['X-Captcha-Token'] = captchaToken;
  if (captchaPass) headers['X-Captcha-Pass'] = captchaPass;
  // Owner preview is this app, top-level or iframed by the preview page.
  // The iframe on another site must not attach the signed-in session.
  if (appOwnsFrame()) {
    const token = await getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const failResponse = async (response, fallback) => {
  let detail = fallback;
  try {
    const body = await response.json();
    if (typeof body.detail === 'string' && body.detail) detail = body.detail;
  } catch {
    // The body is not JSON. Keep the fallback message.
  }
  const error = new Error(detail);
  error.status = response.status;
  throw error;
};

const readNdjson = async (response, onEvent, abortSignal) => {
  if (!response.body) throw new Error('Response body is not available for streaming');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      if (abortSignal?.aborted) {
        reader.cancel();
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.trim()) continue;
        onEvent(JSON.parse(line));
      }
    }
    if (!abortSignal?.aborted && buffer.trim()) onEvent(JSON.parse(buffer.trim()));
  } finally {
    reader.releaseLock();
  }
};

export const embedApi = {
  getCaptcha: async () => {
    const response = await fetch(`${getApiBaseUrl()}/embed/v1/captcha`);
    if (!response.ok) await failResponse(response, 'Captcha is unavailable.');
    return response.json();
  },

  createChat: async (publicKey, captchaToken) => {
    const response = await fetch(`${getApiBaseUrl()}/embed/v1/chats`, {
      method: 'POST',
      headers: await embedHeaders(publicKey, { captchaToken }),
    });
    if (!response.ok) await failResponse(response, 'Could not start the chat');
    return response.json();
  },

  getMap: async (publicKey, mapId, { captchaToken, captchaPass } = {}) => {
    const response = await fetch(`${getApiBaseUrl()}/embed/v1/maps/${mapId}`, {
      headers: await embedHeaders(publicKey, { captchaToken, captchaPass }),
    });
    if (!response.ok) await failResponse(response, 'Could not open the map');
    return response.json();
  },

  streamChat: async ({
    publicKey,
    chatId,
    text,
    captchaPass,
    onEvent,
    abortSignal,
    modelTier = 'deep',
    thinking = false,
  }) => {
    const response = await fetch(`${getApiBaseUrl()}/embed/v1/chats/${chatId}/stream`, {
      method: 'POST',
      headers: await embedHeaders(publicKey, { captchaPass }),
      body: JSON.stringify({ text, datasets: [], model_tier: modelTier, thinking }),
      signal: abortSignal,
    });
    if (!response.ok) await failResponse(response, 'Chat stream failed');
    await readNdjson(response, onEvent, abortSignal);
  },
};
